import { RydProject } from "../model/project";
import { RydTrack } from "../model/tracks";
import { workspaceStore } from "../stores/WorkspaceStore";

export type KeyBindingScopeUnit = 'global' | 'track' | 'selection-active';

export type KeyBindingSpecification = {
    key?: string,
    ctrl?: boolean,
    shift?: boolean,
    alt?: boolean,
    meta?: boolean
}

export type KeyBindingHandlerDefinition<T> = {
    description: string,
    handler: (data?: any) => void,
    scopeUnit: KeyBindingScopeUnit,
    trigger: KeyBindingSpecification
    resolver?: () => T | undefined
}

const defaultModifierOpts = {
    ctrl: false,
    shift: false,
    alt: false,
    meta: false,
};

export class KeybindingsManager {
    private static instance: KeybindingsManager;
    private keyBindings: KeyBindingHandlerDefinition<any>[] = [];
    private scopedData: {
        global?: RydProject,
        track?: RydTrack,
    } = {};

    private constructor() {
        console.log('INIT GLOBAL registering keydown listener');
        window.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.type !== 'keydown') return;
            if (e.repeat) return;
            // console.log('GLOBAL KEYDOWN', e);
            this.dispatch(e);
        });
    }

    public static getInstance() {
        if (!KeybindingsManager.instance) {
            KeybindingsManager.instance = new KeybindingsManager();
        }
        return KeybindingsManager.instance;
    }

    /**
     * FIXME: get the project data from state.
     */
    /* @deprecated */
    setScopedData(data: { global?: RydProject, track?: RydTrack }) {
        console.log('SET SCOPED DATA', data);
        this.scopedData = { ...this.scopedData, ...data };
    }

    resolve<T>(
        keyEvent: KeyboardEvent
    ): {
        handler: ((data?) => void) | undefined,
        scopeUnit: KeyBindingScopeUnit,
        resolver? : () => T | undefined
    } {

        if (workspaceStore.getState().uiState.globalKeybindingsSuspended) {
            // console.log('GLOBAL KEYBINDINGS SUSPENDED');
            return { handler: undefined, scopeUnit: 'global' };
        }

        const r = this.keyBindings.find(def => {
            return (
                (def.trigger.key ? def.trigger.key === keyEvent.key : true) &&
                (def.trigger.ctrl ? def.trigger.ctrl === keyEvent.ctrlKey : true) &&
                (def.trigger.shift ? def.trigger.shift === keyEvent.shiftKey : true) &&
                (def.trigger.alt ? def.trigger.alt === keyEvent.altKey : true) &&
                (def.trigger.meta ? def.trigger.meta === keyEvent.metaKey : true)
            );
        });

        // console.log('RESOLVE', keyEvent, r);
        if (!r) return { handler: undefined, scopeUnit: 'global' };
        return { handler: r.handler, scopeUnit: r.scopeUnit, resolver: r.resolver };
    }

    resetKeybindings() {
        this.keyBindings = [];
        this.scopedData = {};
    }

    dispatch<T>(keyEvent: KeyboardEvent) {
        const { handler, scopeUnit, resolver } = this.resolve(keyEvent);
        if (!handler) return;
        const data = scopeUnit === 'global'
            ? this.scopedData[scopeUnit]
            : resolver?.();
        // console.log('DISPATCH', keyEvent, handler, scopeUnit, data);
        // if (!data) return;
        keyEvent.preventDefault();
        handler(data);
    }

    register(definition: KeyBindingHandlerDefinition<any>) {

        // to allow definitions to be shorter by only specifiying the modifiers they want
        // we merge with the defaultModifierOpts
        const newDef = { ...definition };
        newDef.trigger = {
            ...defaultModifierOpts,
            ...newDef.trigger
        };

        /**
         * we keep the entries sorted by most populated to least populated.
         * This way this.resolve() will do linear search in the most effective way to find the
         * best match for an event
         */
        this.keyBindings = this.keyBindings.concat([newDef])
            .sort((a, b) => {
                if (a.trigger.meta !== b.trigger.meta)
                    return (a.trigger.meta ? -1 : 0) - (b.trigger.meta ? -1 : 0);
                if (a.trigger.ctrl !== b.trigger.ctrl)
                    return (a.trigger.ctrl ? -1 : 0) - (b.trigger.ctrl ? -1 : 0);
                if (a.trigger.alt !== b.trigger.alt)
                    return (a.trigger.alt ? -1 : 0) - (b.trigger.alt ? -1 : 0);
                if (a.trigger.shift !== b.trigger.shift)
                    return (a.trigger.shift ? -1 : 0) - (b.trigger.shift ? -1 : 0);
                if (a.trigger.key !== b.trigger.key)
                    return (a.trigger.key && b.trigger.key) ?
                        a.trigger.key.localeCompare(b.trigger.key) : 0;
                return 0;
            });
    }
}

/**
 * Some keybindings might interact annoyingly with edit fields and other keyboard interactions.
 * Components have the option to suspend global keybindings while they are focused.
 */
export function keybindingsGlobalSuspend() {
    workspaceStore.setState((state) => ({
        uiState: {
            ...state.uiState,
            globalKeybindingsSuspended: true
        }
    }));
}
export function keybindingsGlobalResume() {
    workspaceStore.setState((state) => ({
        uiState: {
            ...state.uiState,
            globalKeybindingsSuspended: false
        }
    }));
}